import type {
  AuthUser,
  PublicConfig,
  TemplateCreateRequest,
  TemplateDetail,
  TemplateListResponse,
} from "../shared/types.ts";
import type { UserPieceData, UserPieceDetail, UserPieceListResponse } from "../shared/user-piece.ts";
import { t } from "./i18n/index.ts";
import { NEARBY_FRESH_PARAM } from "../shared/quest-cache.ts";
import { isLngLat } from "../shared/geo.ts";
import type { PlaceSearchResponse } from "../shared/place-search.ts";
import { questCache } from "./quest/quest-cache.ts";
import type { NearbyQuest, QuestDetail, QuestWriteBody } from "./quest/types.ts";

async function readError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string };
    if (body.error) return body.error;
  } catch {
    /* ignore */
  }
  return t("api.failed", { status: res.status });
}

function apiFetch(input: string, init?: RequestInit): Promise<Response> {
  return fetch(input, { credentials: "same-origin", ...init });
}

export async function getConfig(): Promise<PublicConfig> {
  const res = await apiFetch("/api/config");
  if (!res.ok) throw new Error(await readError(res));
  return res.json() as Promise<PublicConfig>;
}

export async function getMe(): Promise<AuthUser | null> {
  const res = await apiFetch("/api/me");
  if (!res.ok) throw new Error(await readError(res));
  const body = (await res.json()) as { user: AuthUser | null };
  return body.user;
}

export async function signInWithGoogle(idToken: string): Promise<AuthUser> {
  const res = await apiFetch("/api/auth/google", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ id_token: idToken }),
  });
  if (!res.ok) throw new Error(await readError(res));
  const body = (await res.json()) as { user: AuthUser };
  return body.user;
}

export async function signOut(): Promise<void> {
  const res = await apiFetch("/api/auth/logout", { method: "POST" });
  if (!res.ok) throw new Error(await readError(res));
}

export async function createTemplate(
  payload: TemplateCreateRequest,
): Promise<TemplateDetail> {
  const res = await apiFetch("/api/templates", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await readError(res));
  return res.json() as Promise<TemplateDetail>;
}

export async function getTemplate(id: string): Promise<TemplateDetail> {
  const res = await apiFetch(`/api/templates/${encodeURIComponent(id)}`);
  if (!res.ok) throw new Error(await readError(res));
  return res.json() as Promise<TemplateDetail>;
}

export async function listTemplatesBBox(
  bbox: {
    minLat: number;
    minLng: number;
    maxLat: number;
    maxLng: number;
  },
  minTrust = 0,
): Promise<TemplateListResponse> {
  const params = new URLSearchParams({
    minLat: String(bbox.minLat),
    minLng: String(bbox.minLng),
    maxLat: String(bbox.maxLat),
    maxLng: String(bbox.maxLng),
    minTrust: String(minTrust),
  });
  const res = await apiFetch(`/api/templates?${params}`);
  if (!res.ok) throw new Error(await readError(res));
  return res.json() as Promise<TemplateListResponse>;
}

export function gpxUrl(id: string): string {
  return `/api/templates/${encodeURIComponent(id)}?format=gpx`;
}

export async function listUserPieces(): Promise<UserPieceListResponse> {
  const res = await apiFetch("/api/pieces");
  if (!res.ok) throw new Error(await readError(res));
  return res.json() as Promise<UserPieceListResponse>;
}

export async function getUserPiece(id: string): Promise<UserPieceDetail> {
  const res = await apiFetch(`/api/pieces/${encodeURIComponent(id)}`);
  if (!res.ok) throw new Error(await readError(res));
  return res.json() as Promise<UserPieceDetail>;
}

export async function saveUserPiece(
  id: string,
  payload: { name: string; data: UserPieceData },
): Promise<UserPieceDetail> {
  const res = await apiFetch(`/api/pieces/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(await readError(res));
  return res.json() as Promise<UserPieceDetail>;
}

export async function publishUserPiece(id: string): Promise<TemplateDetail> {
  const res = await apiFetch(`/api/pieces/${encodeURIComponent(id)}/publish`, {
    method: "POST",
  });
  if (!res.ok) throw new Error(await readError(res));
  return res.json() as Promise<TemplateDetail>;
}

export async function deleteTemplate(id: string): Promise<void> {
  const res = await apiFetch(`/api/templates/${encodeURIComponent(id)}`, { method: "DELETE" });
  if (!res.ok) throw new Error(await readError(res));
}

export async function deleteUserPiece(id: string): Promise<void> {
  const res = await apiFetch(`/api/pieces/${encodeURIComponent(id)}`, { method: "DELETE" });
  if (!res.ok) throw new Error(await readError(res));
}

/** Nearby quests, from the client cache when a close view was asked within the last minute. */
export async function listNearbyQuests(
  bbox: { minLat: number; minLng: number; maxLat: number; maxLng: number },
  center: { lat: number; lng: number } | null,
): Promise<{ quests: NearbyQuest[] }> {
  const query: Record<string, string> = {
    minLat: String(bbox.minLat),
    minLng: String(bbox.minLng),
    maxLat: String(bbox.maxLat),
    maxLng: String(bbox.maxLng),
  };
  if (center && isLngLat([center.lng, center.lat])) {
    query.lat = String(center.lat);
    query.lng = String(center.lng);
  }
  return questCache.nearby(query, async (fresh) => {
    const params = new URLSearchParams(query);
    if (fresh) params.set(NEARBY_FRESH_PARAM, "1");
    const res = await apiFetch(`/api/quests/nearby?${params}`);
    if (!res.ok) throw new Error(await readError(res));
    return res.json() as Promise<{ quests: NearbyQuest[] }>;
  });
}

/** A quest from the server. Its public copy is kept for `cachedQuest`; a 404 drops it. */
export async function getQuest(id: string): Promise<QuestDetail> {
  const res = await apiFetch(`/api/quests/${encodeURIComponent(id)}`);
  if (!res.ok) {
    if (res.status === 404) await questCache.forget(id);
    throw new ApiError(await readError(res), res.status);
  }
  const quest = (await res.json()) as QuestDetail;
  await questCache.remember(quest);
  return quest;
}

/** A published quest kept by an earlier `getQuest`, to show while `getQuest` revalidates. */
export function cachedQuest(id: string): Promise<QuestDetail | null> {
  return questCache.cachedQuest(id).catch(() => null);
}

/** Store a published quest detail for cache-first home list cards (same rules as `getQuest`). */
export function rememberPublicQuest(quest: QuestDetail): Promise<void> {
  return questCache.remember(quest);
}

/** Clear cached quests, nearby lists and old Piece copies. */
export function clearQuestCache(): Promise<void> {
  return questCache.clear();
}

export function questGpxUrl(id: string): string {
  return `/api/quests/${encodeURIComponent(id)}?format=gpx`;
}

export async function listMyMissions(center: { lat: number; lng: number } | null): Promise<{ missions: QuestDetail[] }> {
  const qs =
    center != null && isLngLat([center.lng, center.lat])
      ? `?${new URLSearchParams({ lat: String(center.lat), lng: String(center.lng) })}`
      : "";
  const res = await apiFetch(`/api/me/missions${qs}`);
  if (!res.ok) throw new Error(await readError(res));
  return res.json() as Promise<{ missions: QuestDetail[] }>;
}

export async function listSavedQuests(center: { lat: number; lng: number } | null): Promise<{ quests: NearbyQuest[] }> {
  const qs =
    center != null && isLngLat([center.lng, center.lat])
      ? `?${new URLSearchParams({ lat: String(center.lat), lng: String(center.lng) })}`
      : "";
  const res = await apiFetch(`/api/me/saved-quests${qs}`);
  if (!res.ok) throw new Error(await readError(res));
  return res.json() as Promise<{ quests: NearbyQuest[] }>;
}

export async function saveQuest(id: string): Promise<void> {
  const res = await apiFetch(`/api/quests/${encodeURIComponent(id)}/save`, { method: "PUT" });
  if (!res.ok) throw new Error(await readError(res));
}

export async function unsaveQuest(id: string): Promise<void> {
  const res = await apiFetch(`/api/quests/${encodeURIComponent(id)}/save`, { method: "DELETE" });
  if (!res.ok) throw new Error(await readError(res));
}

export async function getMission(id: string): Promise<QuestDetail> {
  const res = await apiFetch(`/api/missions/${encodeURIComponent(id)}`);
  if (!res.ok) throw new ApiError(await readError(res), res.status);
  return res.json() as Promise<QuestDetail>;
}

export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

export async function createMission(body: QuestWriteBody): Promise<QuestDetail> {
  const res = await apiFetch("/api/missions", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await readError(res));
  return res.json() as Promise<QuestDetail>;
}

export async function updateMission(id: string, body: QuestWriteBody): Promise<QuestDetail> {
  const res = await apiFetch(`/api/missions/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await readError(res));
  const quest = (await res.json()) as QuestDetail;
  if (quest.status === "active") {
    await questCache.forget(id);
    await questCache.noteOwnChange(id);
  }
  return quest;
}

/** Bind the editor Piece (saved on the server) as this quest draft's route. */
export async function bindQuestRoute(id: string, pieceId: string): Promise<QuestDetail> {
  const res = await apiFetch(`/api/missions/${encodeURIComponent(id)}/route`, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ piece_id: pieceId }),
  });
  if (!res.ok) throw new Error(await readError(res));
  const quest = (await res.json()) as QuestDetail;
  if (quest.status === "active") {
    await questCache.forget(id);
    await questCache.noteOwnChange(id);
  }
  return quest;
}

export async function publishMission(id: string): Promise<QuestDetail> {
  const res = await apiFetch(`/api/missions/${encodeURIComponent(id)}/publish`, { method: "POST" });
  if (!res.ok) throw new Error(await readError(res));
  await questCache.noteOwnChange(id);
  return res.json() as Promise<QuestDetail>;
}

export async function deleteMission(id: string): Promise<void> {
  const res = await apiFetch(`/api/missions/${encodeURIComponent(id)}`, { method: "DELETE" });
  if (!res.ok) throw new Error(await readError(res));
  await questCache.noteOwnChange(id);
}

/** Upload the cropped, encoded cover of a draft; returns its URL. */
export async function uploadQuestCover(id: string, image: Blob): Promise<string> {
  const res = await apiFetch(`/api/missions/${encodeURIComponent(id)}/cover`, {
    method: "PUT",
    headers: { "content-type": image.type || "image/webp" },
    body: image,
  });
  if (!res.ok) throw new Error(await readError(res));
  const body = (await res.json()) as { cover_image: string };
  await questCache.forget(id);
  await questCache.noteOwnChange(id);
  return body.cover_image;
}

export async function fetchLinkPreview(url: string): Promise<{ title: string | null; image: string | null; domain: string }> {
  const params = new URLSearchParams({ url });
  const res = await apiFetch(`/api/link-preview?${params}`);
  if (!res.ok) throw new Error(await readError(res));
  return res.json() as Promise<{ title: string | null; image: string | null; domain: string }>;
}

export async function uploadGalleryPhoto(id: string, blockSlot: number, itemIndex: number, image: Blob): Promise<string> {
  const res = await apiFetch(
    `/api/missions/${encodeURIComponent(id)}/blocks/${blockSlot}/gallery/${itemIndex}/image`,
    {
      method: "PUT",
      headers: { "content-type": image.type || "image/webp" },
      body: image,
    },
  );
  if (!res.ok) throw new Error(await readError(res));
  const body = (await res.json()) as { image: string };
  await questCache.forget(id);
  await questCache.noteOwnChange(id);
  return body.image;
}

export async function uploadQuestPagePhoto(id: string, slot: number, image: Blob): Promise<string> {
  const res = await apiFetch(`/api/missions/${encodeURIComponent(id)}/page-photos/${slot}/image`, {
    method: "PUT",
    headers: { "content-type": image.type || "image/webp" },
    body: image,
  });
  if (!res.ok) throw new Error(await readError(res));
  const body = (await res.json()) as { image: string };
  await questCache.forget(id);
  await questCache.noteOwnChange(id);
  return body.image;
}

/** Upload a staged spot thumbnail (R2 only; committed on quest save). */
export async function uploadQuestSpotThumbnailPending(id: string, seq: number, image: Blob): Promise<string> {
  const res = await apiFetch(`/api/missions/${encodeURIComponent(id)}/spots/${seq}/thumbnail/pending`, {
    method: "PUT",
    headers: { "content-type": image.type || "image/webp" },
    body: image,
  });
  if (!res.ok) throw new Error(await readError(res));
  const body = (await res.json()) as { thumbnail_image: string };
  return body.thumbnail_image;
}

/** Upload a spot checkpoint thumbnail; returns its public URL. */
export async function uploadQuestSpotThumbnail(id: string, seq: number, image: Blob): Promise<string> {
  const res = await apiFetch(`/api/missions/${encodeURIComponent(id)}/spots/${seq}/thumbnail`, {
    method: "PUT",
    headers: { "content-type": image.type || "image/webp" },
    body: image,
  });
  if (!res.ok) throw new Error(await readError(res));
  const body = (await res.json()) as { thumbnail_image: string };
  await questCache.forget(id);
  await questCache.noteOwnChange(id);
  return body.thumbnail_image;
}

export async function uploadQuestSpotPhoto(id: string, seq: number, slot: number, image: Blob): Promise<string> {
  const res = await apiFetch(
    `/api/missions/${encodeURIComponent(id)}/spots/${seq}/images/${slot}/image`,
    {
      method: "PUT",
      headers: { "content-type": image.type || "image/webp" },
      body: image,
    },
  );
  if (!res.ok) throw new Error(await readError(res));
  const body = (await res.json()) as { image: string };
  await questCache.forget(id);
  await questCache.noteOwnChange(id);
  return body.image;
}

export async function deleteQuestSpotPhoto(id: string, seq: number, slot: number): Promise<void> {
  const res = await apiFetch(`/api/missions/${encodeURIComponent(id)}/spots/${seq}/images/${slot}`, {
    method: "DELETE",
  });
  if (!res.ok && res.status !== 204) throw new Error(await readError(res));
  await questCache.forget(id);
  await questCache.noteOwnChange(id);
}

/** Forward place search for spot locations (proxied through the Worker). */
export async function searchPlaces(q: string, lang: string): Promise<PlaceSearchResponse> {
  const params = new URLSearchParams({ q, lang });
  const res = await apiFetch(`/api/geocode/search?${params}`);
  if (!res.ok) throw new Error(await readError(res));
  return res.json() as Promise<PlaceSearchResponse>;
}

/** Remove a quest's cover (its author on a draft, or an admin). */
export async function removeQuestCover(id: string): Promise<void> {
  const res = await apiFetch(`/api/missions/${encodeURIComponent(id)}/cover`, { method: "DELETE" });
  if (!res.ok) throw new Error(await readError(res));
  await questCache.forget(id);
}

export async function reportQuest(id: string, reason: string): Promise<void> {
  const res = await apiFetch(`/api/missions/${encodeURIComponent(id)}/reports`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ reason }),
  });
  if (!res.ok) throw new ApiError(await readError(res), res.status);
}

export interface QuestLine {
  id: string;
  body: string;
}

export interface QuestCompletion {
  name: string | null;
  picture: string | null;
  completed_at: string;
  line: QuestLine | null;
  /** The signed-in viewer's own row. */
  mine: boolean;
}

export interface CompletionViewer {
  completed: boolean;
  line: QuestLine | null;
  can_write: boolean;
  admin: boolean;
}

export interface CompletionPage {
  total: number;
  completions: QuestCompletion[];
  viewer: CompletionViewer | null;
}

/** People who finished a published quest, newest first; `total` counts them all. */
export async function listCompletions(id: string, offset: number, limit: number): Promise<CompletionPage> {
  const params = new URLSearchParams({ offset: String(offset), limit: String(limit) });
  const res = await apiFetch(`/api/quests/${encodeURIComponent(id)}/completions?${params}`);
  if (!res.ok) throw new ApiError(await readError(res), res.status);
  return res.json() as Promise<CompletionPage>;
}

/** Write or replace the viewer's one line on a quest they finished. */
export async function saveQuestLine(id: string, body: string): Promise<QuestLine> {
  const res = await apiFetch(`/api/quests/${encodeURIComponent(id)}/line`, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ body }),
  });
  if (!res.ok) throw new ApiError(await readError(res), res.status);
  return res.json() as Promise<QuestLine>;
}

export async function deleteQuestLine(id: string): Promise<void> {
  const res = await apiFetch(`/api/quests/${encodeURIComponent(id)}/line`, { method: "DELETE" });
  if (!res.ok) throw new ApiError(await readError(res), res.status);
}

export async function reportQuestLine(id: string, lineId: string, reason: string): Promise<void> {
  const res = await apiFetch(
    `/api/quests/${encodeURIComponent(id)}/lines/${encodeURIComponent(lineId)}/reports`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ reason }),
    },
  );
  if (!res.ok) throw new ApiError(await readError(res), res.status);
}

/** Admins: remove someone's line. */
export async function removeQuestLine(lineId: string): Promise<void> {
  const res = await apiFetch(`/api/admin/quest-lines/${encodeURIComponent(lineId)}`, { method: "DELETE" });
  if (!res.ok) throw new ApiError(await readError(res), res.status);
}

/** Debug: whether IndexedDB has a published quest detail for this id (public-shaped row). */
export async function questCacheHas(id: string): Promise<boolean> {
  return (await cachedQuest(id)) != null;
}

declare global {
  interface Window {
    __mapusQuestCacheHas?: (id: string) => Promise<boolean>;
  }
}

if (typeof window !== "undefined") {
  window.__mapusQuestCacheHas = (id) => questCacheHas(id);
}
